import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { User } from '../App';
import { ShareModal } from './ShareModal';
import { toast } from 'sonner';
import { BonusPanel } from './BonusPanel';
import { MexcClaim } from './MexcClaim';
import { i18n } from '../utils/i18n';
import { 
  Users, 
  Gift, 
  TrendingUp, 
  Trophy, 
  Copy,
  Settings as SettingsIcon,
  ArrowLeft,
  QrCode,
  X,
  Lock,
  CheckCircle2
} from 'lucide-react';

interface ReferralUser {
  id: number;
  nickname: string;
  referralCode: string;
  joinDate: string;
  totalEarnings: number;
  level: number;
}

interface RewardsProps {
  user: User;
  onSettingsClick: () => void;
}

export function Rewards({ user, onSettingsClick }: RewardsProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState<'rewards' | 'assets' | 'bonus'>('rewards');
  // 보너스 지급 권한. 어드민이 준 사람에게만 탭이 보인다.
  const [bonusPerm, setBonusPerm] = useState<any>(null);
  // 추천코드 QR 이미지(실제 QR). 예전엔 아이콘만 띄우는 껍데기였다.
  const [qrImage, setQrImage] = useState<string>('');
  // 추천코드 QR 스캔 — 관리자뿐 아니라 일반 회원도 상대 QR 을 찍어 코드를 넣을 수 있어야 한다
  const [refScanning, setRefScanning] = useState(false);
  const refScannerRef = useRef<any>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  // 새로 추가된 상태들
  const [referralUsers, setReferralUsers] = useState<ReferralUser[]>([]);
  const [assetHistory, setAssetHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [handyPrice, setHandyPrice] = useState<number | null>(null);
  const [pendingBonus, setPendingBonus] = useState<number>(user.pendingSignupBonus || 0);
  // 총자산은 홈 화면과 같은 공식: 사용가능 + 락업 (같은 API)
  const [lockedHandy, setLockedHandy] = useState(0);
  React.useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token') || '';
        if (!t) return;
        const r = await fetch('/api/wallet-lockups', { headers: { Authorization: `Bearer ${t}` } });
        const d = await r.json();
        if (d?.success) setLockedHandy(Number(d.totalLocked || 0));
      } catch { /* 조회 실패는 화면을 막지 않는다 */ }
    })();
  }, [user.walletAddress]);
  const walletBal = Number(user.balance || 0) + lockedHandy;
  const totalHoldings = walletBal + Number(pendingBonus || 0);
  const [referredBy, setReferredBy] = useState<string | null>(user.referredBy || null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showActivated, setShowActivated] = useState(false);
  const [activatedAmount, setActivatedAmount] = useState(0);
  const [rewardPolicy, setRewardPolicy] = useState<{ signup_bonus_amount: string; referrer_bonus_amount: string } | null>(null);

  // 어드민 보상정책(reward_config) 공개 조회 - 화면에 하드코딩된 금액을 쓰지 않기 위함
  useEffect(() => {
    fetch('/api/referrals/reward-policy')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success) setRewardPolicy(d.policy); })
      .catch(() => {});
  }, []);

  // 코인게코 실시간 가격 조회
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=handy&vs_currencies=usd');
        if (res.ok) {
          const data = await res.json();
          if (data.handy) setHandyPrice(data.handy.usd);
        }
      } catch {}
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleVerifyReferral = async () => {
    if (!verifyCode.trim()) {
      toast.error('추천코드를 입력하세요.');
      return;
    }
    if (verifying) return;
    setVerifying(true);
    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error('로그인이 필요합니다.');
        setVerifying(false);
        return;
      }
      const API_URL = (import.meta as any).env?.VITE_API_URL || '';

      const validateRes = await fetch(`${API_URL}/api/referrals/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ referralCode: verifyCode.trim().toUpperCase() })
      });
      if (!validateRes.ok) {
        const err = await validateRes.json().catch(() => ({}));
        throw new Error(err.error || '추천코드 검증에 실패했습니다.');
      }
      const validateData = await validateRes.json();
      if (!validateData.valid) {
        toast.error('유효하지 않은 추천코드입니다.');
        setVerifying(false);
        return;
      }

      const applyRes = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ referralCode: verifyCode.trim().toUpperCase() })
      });
      if (!applyRes.ok) {
        const err = await applyRes.json().catch(() => ({}));
        throw new Error(err.error || '추천코드 인증에 실패했습니다.');
      }
      const applyData = await applyRes.json();

      // 인증 완료 화면을 띄운다. 바로 새로고침하면 메인으로 점프해 인지가 안 되므로
      // 사용자가 확인을 누른 뒤에 갱신한다.
      setActivatedAmount(Number(applyData.activatedBonus ?? pendingBonus));
      setShowActivated(true);
      setReferredBy(verifyCode.trim().toUpperCase());
      setPendingBonus(0);
      setVerifyCode('');
    } catch (error: any) {
      toast.error(error.message || '추천코드 인증 중 오류가 발생했습니다.');
    } finally {
      setVerifying(false);
    }
  };

  // 실제 추천 현황 로드 (/api/referrals/my-referrals) - 이전엔 존재하지 않는
  // /api/rewards/referrals를 호출해서 항상 실패했고, 화면엔 하드코딩된 100/130이 보였음
  const [referralSummary, setReferralSummary] = useState<{
    totalReferrals: number;
    activeReferrals: number;
    totalEarnings: number;
    pendingRewards: number;
    tier: string;
  }>({ totalReferrals: 0, activeReferrals: 0, totalEarnings: 0, pendingRewards: 0, tier: 'BRONZE' });

  const loadReferrals = React.useCallback(async () => {
    if (!user?.walletAddress) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/referrals/my-referrals/${user.walletAddress}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          const s = data.data.summary;
          setReferralSummary({
            totalReferrals: s.total_referrals || 0,
            activeReferrals: s.active_referrals || 0,
            totalEarnings: s.total_earnings || 0,
            pendingRewards: s.pending_rewards || 0,
            tier: s.tier || 'BRONZE'
          });
          setReferralUsers(data.data.referrals || []);
        }
      }
    } catch (error) {
      console.error('추천인 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.walletAddress]);

  useEffect(() => { loadReferrals(); }, [loadReferrals]);

  // 자산 변경 내역 로드
  useEffect(() => {
    const loadAssetHistory = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('handy-jwt-token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/rewards/asset-history/${user.walletAddress}?limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAssetHistory(data);
        }
      } catch (error) {
        console.error('자산 내역 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user.walletAddress) {
      loadAssetHistory();
    }
  }, [user.walletAddress]);

  // 추천 코드 복사
  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode || '');
      toast.success('추천코드가 복사되었습니다.');
    } catch (error) {
      console.error('복사 실패:', error);
      toast.error('복사에 실패했습니다.');
    }
  };

  // QR 코드 표시
  // QR(또는 공유 링크)로 들어오면 추천코드를 자동으로 채운다.
  // 예전엔 ?ref= 를 읽는 코드가 없어서 QR 을 찍어도 아무 일도 일어나지 않았다.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const code = (p.get('ref') || p.get('code') || '').trim().toUpperCase();
      if (code && !referredBy) {
        setVerifyCode(code);
        toast.info(`추천코드 ${code} 가 입력되었습니다. 인증하기를 눌러주세요.`);
        // 주소창을 깨끗하게 — 새로고침 때마다 다시 뜨지 않게 한다
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch { /* 파라미터 파싱 실패는 무시 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** QR 에서 추천코드만 뽑는다. URL 이든 코드 자체든 다 받는다. */
  const extractCode = (raw: string): string => {
    const v = String(raw || '').trim();
    try {
      const u = new URL(v);
      const q = (u.searchParams.get('ref') || u.searchParams.get('code') || '').trim();
      if (q) return q.toUpperCase();
    } catch { /* URL 이 아니면 아래로 */ }
    const m = v.match(/[A-Za-z0-9]{6}/);
    return (m ? m[0] : v).toUpperCase();
  };

  const stopRefScan = async () => {
    try { await refScannerRef.current?.stop(); } catch { /* 이미 꺼짐 */ }
    try { refScannerRef.current?.clear(); } catch { /* noop */ }
    refScannerRef.current = null;
    setRefScanning(false);
  };
  useEffect(() => () => { stopRefScan(); }, []);

  const startRefScan = async () => {
    setRefScanning(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const sc = new Html5Qrcode('ref-qr-reader');
      refScannerRef.current = sc;
      await sc.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (text: string) => {
          await stopRefScan();
          const code = extractCode(text);
          setVerifyCode(code);
          toast.success(`추천코드 ${code} 인식`);
        },
        () => { /* 인식 실패 프레임은 무시 */ },
      );
    } catch {
      await stopRefScan();
      toast.error('카메라를 쓸 수 없습니다. 코드를 직접 입력해주세요.');
    }
  };

  const authHeaders = (): Record<string, string> => {
    const t = localStorage.getItem('handy-jwt-token') || localStorage.getItem('token') || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    fetch('/api/bonus/my-permission', { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setBonusPerm(d && d.success ? d : null))
      .catch(() => {});
  }, []);

  const showReferralQR = async () => {
    const url = `${window.location.origin}/?ref=${user.referralCode}`;
    setQrCodeUrl(url);
    setShowQRModal(true);
    try {
      // 예전엔 아이콘만 띄워서 실제로는 스캔이 안 됐다. 진짜 QR 을 그린다.
      const QRCode = await import('qrcode');
      setQrImage(await QRCode.toDataURL(url, { width: 384, margin: 1 }));
    } catch {
      setQrImage('');
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="px-4 py-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-light text-foreground">보상</h1>
            <p className="text-muted-foreground">추천 현황과 보상을 관리하세요</p>
          </div>
          <button
            onClick={onSettingsClick}
            className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <SettingsIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-muted mx-4 mt-4 mb-4 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('rewards')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeTab === 'rewards'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Gift className="w-4 h-4" />
            <span className="text-sm font-medium">보상</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('assets')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeTab === 'assets'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">자산 관리</span>
          </div>
        </button>
        {/* 보너스 탭 — 어드민이 지급 권한을 준 사람에게만 보인다 */}
        {bonusPerm?.allowed && (
          <button
            onClick={() => setActiveTab('bonus')}
            className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
              activeTab === 'bonus'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Gift className="w-4 h-4" />
              <span className="text-sm font-medium">보너스</span>
            </div>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {activeTab === 'bonus' ? <BonusPanel authHeaders={authHeaders} />
         : activeTab === 'rewards' ? (
          <div className="space-y-6">
            {/* MEXC 가입 이벤트 — 이벤트가 꺼져 있으면 컴포넌트가 스스로 숨는다 */}
            <MexcClaim authHeaders={authHeaders} />
            {/* 총 잔액 + 추천코드 인증 (회원가입 에어드롭 활성화) */}
            <div
              style={{
                background: '#E85D00',
                borderRadius: '12px',
                padding: '20px',
                color: '#FFFFFF'
              }}
            >
              <p style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px', lineHeight: 1.5 }}>
                총 잔액
              </p>
              <p style={{ fontSize: '30px', fontWeight: 700, lineHeight: 1.4, margin: 0 }}>
                {totalHoldings.toFixed(2)}
                <span style={{ fontSize: '16px', fontWeight: 600, marginLeft: '6px' }}>wHANDY</span>
              </p>
              {handyPrice !== null && (
                <p style={{ fontSize: '15px', marginTop: '8px', opacity: 0.95 }}>
                  ≈ ${(totalHoldings * handyPrice).toFixed(2)}
                </p>
              )}
            </div>

            {!referredBy && (
              <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Lock className="w-5 h-5 text-amber-600" />
                    <h3 className="font-bold text-amber-800 dark:text-amber-300">추천코드 인증</h3>
                  </div>
                  <p className="text-amber-700 dark:text-amber-400 text-sm mb-3">
                    {pendingBonus > 0
                      ? `인증하면 에어드롭 ${pendingBonus.toLocaleString()} wHANDY가 활성화됩니다.`
                      : '추천코드를 인증하면 에어드롭이 활성화됩니다.'}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                    <input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                      placeholder="추천코드 입력"
                      maxLength={10}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '46px',
                        padding: '0 12px',
                        borderRadius: '10px',
                        border: '1px solid #FCD34D',
                        fontSize: '15px',
                        background: '#FFFFFF',
                        color: '#111827'
                      }}
                    />
                    <button
                      onClick={handleVerifyReferral}
                      disabled={verifying || !verifyCode.trim()}
                      style={{
                        flexShrink: 0,
                        height: '46px',
                        padding: '0 20px',
                        borderRadius: '10px',
                        background: verifying || !verifyCode.trim() ? '#D1A054' : '#D97706',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '15px',
                        whiteSpace: 'nowrap',
                        border: 'none'
                      }}
                    >
                      {verifying ? '인증 중' : '인증하기'}
                    </button>
                  </div>

                  {/* QR 스캔 — 상대 코드를 눈으로 보고 타이핑할 필요가 없게 한다 */}
                  {!refScanning && (
                    <button
                      onClick={startRefScan}
                      style={{
                        marginTop: '8px', width: '100%', height: '44px',
                        borderRadius: '10px', border: '1px solid #FCD34D',
                        background: '#FFFBEB', color: '#B45309',
                        fontWeight: 700, fontSize: '14px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      }}
                    >
                      <QrCode className="w-4 h-4" /> 추천인 QR 스캔하기
                    </button>
                  )}
                  {refScanning && (
                    <div style={{ marginTop: '8px', position: 'relative',
                                  borderRadius: '10px', overflow: 'hidden', background: '#000' }}>
                      <div id="ref-qr-reader" style={{ width: '100%' }} />
                      <button onClick={stopRefScan}
                        style={{ position: 'absolute', top: '8px', right: '8px',
                                 background: 'rgba(0,0,0,.6)', color: '#fff',
                                 border: 'none', borderRadius: '999px', padding: '6px 10px' }}>
                        닫기
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {referredBy && (
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800">
                <CardContent className="p-4 flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="text-green-800 dark:text-green-300 text-sm font-medium">
                    추천코드 인증 완료 — 회원가입 에어드롭이 활성화되었습니다.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* My Referral Code */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">내 추천코드</h3>
                  <div className="bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-center space-x-3 mb-2">
                      <div className="text-3xl font-bold text-orange-600">{user.referralCode || 'N/A'}</div>
                      <Button
                        onClick={showReferralQR}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto"
                      >
                        <QrCode className="w-6 h-6 text-orange-600" />
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-sm">코드를 공유하면 추천 보상을 받습니다</p>
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="flex-1"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      코드 복사
                    </Button>
                    <Button
                      onClick={() => setShowShareModal(true)}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      공유하기
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{referralSummary.totalReferrals}</div>
                  <p className="text-blue-800 dark:text-blue-300 text-sm">총 추천 인원</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-1">{referralSummary.totalEarnings.toFixed(0)}</div>
                  <p className="text-green-800 dark:text-green-300 text-sm">누적 추천 수익 (wHANDY)</p>
                </CardContent>
              </Card>
            </div>

            {/* 추천 보상 안내 - 어드민 보상정책 설정값을 실시간으로 표시 */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-4">추천 보상 안내</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-foreground">회원가입 에어드롭</span>
                    <span className="font-semibold text-green-600">
                      {rewardPolicy ? Number(rewardPolicy.signup_bonus_amount).toLocaleString() : '-'} wHANDY
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-foreground">추천인 보너스 (친구 1명당)</span>
                    <span className="font-semibold text-blue-600">
                      {rewardPolicy ? Number(rewardPolicy.referrer_bonus_amount).toLocaleString() : '-'} wHANDY
                    </span>
                  </div>
                  {/* 미청구 추천 보상 — 커미션 정책 보류로 노출 제외 (2026-10-14) */}

                </div>
              </CardContent>
            </Card>

            {/* Referral Tree Section */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-foreground">내 추천 현황</h3>
                  {referralUsers.length > 0 && (
                    <span
                      className="text-sm font-bold px-2.5 py-1 rounded-full"
                      style={{ background: '#FFF4E8', color: '#FF6B00' }}
                    >
                      총 {referralUsers.length}명
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm mb-4">내 코드로 가입한 친구 목록</p>

                {/* [2026-09-11 버그수정] referralUsers 에 API 결과가 담기는데 화면에
                    렌더링하는 코드가 아예 없어서, 추천인이 있어도 항상 "아직 추천한
                    친구가 없습니다" 빈 화면만 보였다. */}
                <div className="space-y-3">
                  {referralUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-6xl mb-4">🌳</div>
                      <p className="text-muted-foreground">아직 추천한 친구가 없습니다</p>
                      <p className="text-muted-foreground text-sm">내 추천코드를 공유해보세요!</p>
                    </div>
                  ) : (
                    referralUsers.map((r: any, idx: number) => {
                      const addr = r.wallet_address || '';
                      const shortAddr = addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';
                      const joined = r.joined_at
                        ? new Date(r.joined_at).toLocaleDateString('ko-KR', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })
                        : '';
                      return (
                        <div
                          key={r.id ?? addr ?? idx}
                          className="flex items-center justify-between rounded-2xl p-3"
                          style={{ background: '#F7F7F8' }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white font-bold"
                              style={{ background: '#FF6B00' }}
                            >
                              {(r.nickname || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-foreground truncate">
                                {r.nickname || '익명'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {joined}
                                {shortAddr ? ` · ${shortAddr}` : ''}
                              </p>
                            </div>
                          </div>
                          <span
                            className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
                            style={
                              r.status === 'active'
                                ? { background: '#E9F8EF', color: '#10A45A' }
                                : { background: '#EFEFEF', color: '#8A8A8A' }
                            }
                          >
                            {r.status === 'active' ? '가입완료' : '대기'}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 멤버십 등급 */}
            <Card className="bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-800/20 border-yellow-200 dark:border-yellow-800">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-3">
                  <Trophy className="w-8 h-8 text-yellow-600 mr-2" />
                  <h3 className="font-bold text-yellow-800 dark:text-yellow-300">멤버십 등급</h3>
                </div>
                <div className="text-4xl font-bold text-yellow-600">{referralSummary.tier || 'BRONZE'}</div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Portfolio Overview */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-indigo-900/20 dark:to-purple-800/20 border-indigo-200 dark:border-indigo-800">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-indigo-600 mb-2">
                  {walletBal.toFixed(2)} wHANDY
                </div>
                {handyPrice !== null && (
                  <p className="text-muted-foreground text-sm">≈ ${(walletBal * handyPrice).toFixed(2)}</p>
                )}
              </CardContent>
            </Card>

            {/* Asset Breakdown - 핸디코인 보유내역 */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-4">핸디코인 보유내역</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-border">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">VT</span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">wHANDY</p>
                        <p className="text-muted-foreground text-sm">Handy Token</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground">{walletBal.toFixed(2)} wHANDY</p>
                      {handyPrice !== null && (
                        <p className="text-muted-foreground text-sm">≈ ${(walletBal * handyPrice).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 추천코드 인증 - 회원가입 에어드롭 활성화 (pending_signup_bonus > 0일 때만 노출) */}
            {pendingBonus > 0 && !referredBy && (
              <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Lock className="w-5 h-5 text-amber-600" />
                    <h3 className="font-bold text-amber-800 dark:text-amber-300">추천코드 인증필요</h3>
                  </div>
                  <p className="text-amber-700 dark:text-amber-400 text-sm mb-3">
                    인증하면 에어드롭 {pendingBonus.toLocaleString()} wHANDY가 활성화됩니다.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                    <input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                      placeholder="추천코드 입력"
                      maxLength={10}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '46px',
                        padding: '0 12px',
                        borderRadius: '10px',
                        border: '1px solid #FCD34D',
                        fontSize: '15px',
                        background: '#FFFFFF',
                        color: '#111827'
                      }}
                    />
                    <button
                      onClick={handleVerifyReferral}
                      disabled={verifying || !verifyCode.trim()}
                      style={{
                        flexShrink: 0,
                        height: '46px',
                        padding: '0 20px',
                        borderRadius: '10px',
                        background: verifying || !verifyCode.trim() ? '#D1A054' : '#D97706',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '15px',
                        whiteSpace: 'nowrap',
                        border: 'none'
                      }}
                    >
                      {verifying ? '인증 중' : '인증하기'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}
            {referredBy && (
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800">
                <CardContent className="p-4 flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="text-green-800 dark:text-green-300 text-sm font-medium">추천코드 인증 완료 — 회원가입 에어드롭이 활성화되었습니다.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* 추천코드 인증 완료 */}
      {showActivated && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                 style={{ background: '#16A34A' }}>
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">추천코드 인증 완료</h2>
            <p className="text-base text-gray-600 mb-4">에어드롭이 활성화되었습니다</p>

            <div className="rounded-xl p-5 mb-5"
                 style={{ background: 'linear-gradient(135deg, #FFF4E6, #FFE4CC)' }}>
              <p className="text-base font-semibold text-orange-800 mb-1">활성화된 금액</p>
              <p className="text-4xl font-black" style={{ color: '#E65C00' }}>
                {activatedAmount.toLocaleString()}
              </p>
              <p className="text-lg font-bold text-orange-800 mt-1">wHANDY</p>
            </div>

            <button
              onClick={() => { setShowActivated(false); window.location.reload(); }}
              className="w-full h-14 text-white font-bold text-lg rounded-xl shadow-lg"
              style={{ background: '#FF6B00' }}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          user={user}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground">추천코드 QR</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="text-center">
              <div className="w-48 h-48 bg-white rounded-xl mx-auto mb-4 flex items-center justify-center border-2 border-border overflow-hidden">
                {qrImage
                  ? <img src={qrImage} alt="추천코드 QR" className="w-full h-full object-contain" />
                  : <QrCode className="w-32 h-32 text-gray-400 animate-pulse" />}
              </div>
              <p className="text-muted-foreground text-sm">신규 가입 · 보너스 받을 때 이 QR 을 보여주세요</p>
              <p className="font-mono text-foreground mt-2">{user.referralCode}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
